import React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { authStateAtom, type User as AuthUser } from "@/stores/auth.atom";

// Types
export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  user: AuthUser;
}

// Mock data
const mockAuthResponse: AuthResponse = {
  access_token: "mock_access_token",
  refresh_token: "mock_refresh_token",
  user: {
    id: "google-123456789",
    username: "demo_user",
    email: "user@example.com",
    first_name: "Demo",
    last_name: "User",
    display_name: "Demo User",
    bio: "",
    avatar_url: "https://ui-avatars.com/api/?name=Demo+User&background=random",
    is_staff: false,
    is_verified: false,
    joined_at: new Date().toISOString(),
    last_login: new Date().toISOString(),
    post_count: 0,
    communities: [],
    location: undefined,
  },
};

// Mock API functions
const mockGoogleLogin = async (code: string): Promise<AuthResponse> => {
  console.log("Google Auth Code:", code);
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 1000));
  return mockAuthResponse;
};

const mockFacebookLogin = async (
  accessToken: string,
): Promise<AuthResponse> => {
  console.log("Facebook Access Token:", accessToken);
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 1000));
  return mockAuthResponse;
};

const mockGetCurrentUser = async (): Promise<AuthUser> => {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 500));
  return mockAuthResponse.user;
};

// Query Hooks
export const useGoogleLogin = () => {
  const setAuthState = useSetAtom(authStateAtom);

  return useMutation({
    mutationFn: mockGoogleLogin,
    onSuccess: (data) => {
      // Save tokens
      localStorage.setItem("auth_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);

      // Update auth state
      setAuthState({
        user: data.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    },
  });
};

export const useFacebookLogin = () => {
  const setAuthState = useSetAtom(authStateAtom);

  return useMutation({
    mutationFn: mockFacebookLogin,
    onSuccess: (data) => {
      // Save tokens
      localStorage.setItem("auth_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);

      // Update auth state
      setAuthState({
        user: data.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    },
  });
};

export const useCurrentUser = () => {
  const setAuthState = useSetAtom(authStateAtom);

  const query = useQuery({
    queryKey: ["currentUser"],
    queryFn: mockGetCurrentUser,
    enabled: !!localStorage.getItem("auth_token"),
    retry: 1,
  });

  // Handle success/error using useEffect pattern
  React.useEffect(() => {
    if (query.data) {
      setAuthState((prev) => ({
        ...prev,
        user: query.data,
        isAuthenticated: true,
        isLoading: false,
      }));
    }
  }, [query.data, setAuthState]);

  React.useEffect(() => {
    if (query.error) {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: query.error.message || null,
      });
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refresh_token");
    }
  }, [query.error, setAuthState]);

  return query;
};
